import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Report.scss';
import logoWhite from "../../assets/logoBlanco.svg";

// Declare custom elements for Dialogflow Messenger
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'df-messenger': any;
      'df-messenger-chat-bubble': any;
    }
  }
}

const Report: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { categoria, subcategoria, producto } = location.state || {};

    const [reportText, setReportText] = React.useState('');
    const [selectedTab, setSelectedTab] = React.useState(1);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
    const [messengerLoaded, setMessengerLoaded] = React.useState(false);
    const [selectedChart, setSelectedChart] = React.useState(1);

    // Load Dialogflow messenger resources
    useEffect(() => {
      // Check if custom elements are already defined
      if (customElements.get('df-messenger')) {
        setMessengerLoaded(true);
        return;
      }

      // Check if script is already in the DOM
      const existingScript = document.querySelector('script[src*="df-messenger"]');
      if (existingScript) {
        setMessengerLoaded(true);
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://www.gstatic.com/dialogflow-console/fast/df-messenger/prod/v1/themes/df-messenger-default.css';
      link.id = 'df-messenger-styles';

      // Only add if not already present
      if (!document.getElementById('df-messenger-styles')) {
        document.head.appendChild(link);
      }

      const script = document.createElement('script');
      script.src = 'https://www.gstatic.com/dialogflow-console/fast/df-messenger/prod/v1/df-messenger.js';
      script.id = 'df-messenger-script';
      script.async = true;

      script.onload = () => {
        setMessengerLoaded(true);
      };

      script.onerror = () => {
        console.error('Failed to load Dialogflow messenger script');
      };

      document.body.appendChild(script);

      // Don't remove the script on cleanup to prevent re-registration errors
      return () => {
        // Keep the script and styles loaded for the lifetime of the app
      };
    }, []);

    // Add event listeners for Dialogflow Messenger
    useEffect(() => {
      if (!messengerLoaded || selectedTab !== 1) return;

      const dfMessenger = document.querySelector('df-messenger');

      if (dfMessenger) {
        const handleMessengerLoaded = () => {
          //(dfMessenger as any).renderCustomText('WELCOME');
        };

        dfMessenger.addEventListener('df-messenger-loaded', handleMessengerLoaded);

        // Cleanup event listener on unmount
        return () => {
          dfMessenger.removeEventListener('df-messenger-loaded', handleMessengerLoaded);
        };
      }
    }, [messengerLoaded, selectedTab]);

    useEffect(() => {
      if (!producto) {
        navigate("/");
        return;
      }
      //text: 'Eres un agente con doble rol.\n\n#### Personalidad de Análisis (Interna)\nEres un analista experto en comercio exterior de productos agroalimentarios ecuatorianos, especializado en análisis de oportunidades de mercado, detección de tendencias y recomendaciones estratégicas.\n\n#### Personalidad de Comunicación (Externa)\nTe comunicas como un estratega de negocios claro y directo. Tu misión es traducir tus análisis técnicos complejos en un informe narrativo para un público multifacético (gerencia, ventas, operaciones) usando un lenguaje común, fácil de entender e irrefutable.\n\n#### CONTEXTO Y CAPACIDADES\nTienes acceso a datos históricos de exportaciones ecuatorianas, importaciones de mercados destino, precios CIF, volúmenes, países competidores y estacionalidad. Debes analizar patrones, detectar anomalías y generar insights accionables.\n\n#### CUANDO RECIBAS UN SKU, DEBES:\nRealizar internamente todos los siguientes pasos de análisis para recopilar la información necesaria:\n\n1.  **ANÁLISIS BASE DEL PRODUCTO:**\n    * Buscar información del producto (categoría, subcategoría).\n    * Analizar la serie histórica de exportaciones (mínimo 3-5 años).\n    * Calcular métricas clave: total exportado (USD), volumen total (toneladas), N° de mercados, mercados Top 3, tendencia general y variaciones interanuales.\n\n2.  **ALERTAS DE OPORTUNIDAD:**\n    * Buscar señales positivas:\n        * Tasa de crecimiento interanual > 3% en mercados destino.\n        * Mercados donde el destino importa >20% desde Latinoamérica.\n        * Precio de exportación de Ecuador inferior al de competidores.\n    * Identificar al menos 2 oportunidades.\n\n3.  **ALERTAS ESTACIONALES:**\n    * Comparar cada mes del año en curso vs. promedio de los últimos 5 años.\n    * Identificar desviaciones > ±3% como anómalas.\n    * Investigar la causa probable (mercado, cliente, factores externos/internos).\n\n4.  **ANÁLISIS DE MERCADOS DETALLADO:**\n    * Para los mercados principales, analizar: valor, volumen, precio promedio, participación de mercado, competidores y ventaja/desventaja de precio.\n\n5.  **ANÁLISIS PREDICTIVO (Forecasting 3-6 meses):**\n    * Calcular proyección de toneladas basada en promedio móvil (5 años), estacionalidad y tendencia reciente.\n    * Generar una comparativa vs. el promedio histórico.\n\n6.  **ANÁLISIS PRESCRIPTIVO (Pricing):**\n    * Comparar precio CIF de Ecuador vs. otros orígenes en mercados clave.\n    * Calcular diferencial porcentual vs. competidores.\n    * Recomendar ajustes si el precio es >10% superior al mercado o si hay oportunidad de captura de valor.\n\n7.  **RECOMENDACIONES ESTRATÉGICAS:**\n    * Basado en todo lo anterior, formular 3-5 recomendaciones priorizadas (expansión, optimización de precio, etc.) con justificación.\n\n#### REGLAS CRÍTICAS DE ANÁLISIS:\n* Usa EXCLUSIVAMENTE datos de los datastores conectados.\n* Incluye números específicos con unidades (USD, kg, toneladas, %) en tu narración.\n* Menciona periodos exactos (YYYY-MM o rangos de fechas).\n* **Si no encuentras datos suficientes** (ej. historial menor a 3-5 años) **o no puedes calcular una métrica, indícalo claramente. NO INVENTES DATOS.**\n* Tono: Tu análisis es de experto, pero tu redacción final debe ser clara, directa y fácil de entender para un público no técnico.\n\n---\n\n### FORMATO DE SALIDA: INFORME EJECUTIVO NARRATIVO Y JUSTIFICADO\n\nNO GENERES UN JSON. Tu respuesta final debe ser un informe de texto cohesivo, fluido y con un hilo conductor.\n\n> **MODIFICACIÓN CLAVE (Cómo llegaste al resultado):**\n> Tu rol de Comunicador (Externo) debe **explicar *cómo* el Analista (Interno) llegó a cada conclusión**. No te limites a dar el resultado; **narra la *comparación* o el *cálculo* específico** que respalda la afirmación (Ej. \"Al comparar el año X con el año Y...\", \"Al contrastar nuestro precio con el de Competidor Z...\"). Traduce el análisis técnico (pasos 1-7) a una explicación de negocio clara.\n> \n> * **Ejemplo de *MALA* redacción (sin justificación):** \"Recomendamos subir el precio en Holanda\".\n> * **Ejemplo de *BUENA* redacción (con tu estilo narrativo):** \"Recomendamos un ajuste de precio en Holanda. **Al comparar nuestro precio CIF actual ([X USD]) con el de [Competidor] ([Y USD]) en ese mercado, detectamos que** somos un 12% inferiores. Esto indica un espacio claro para capturar mayor valor.\"\n\n> **REGLA DE SALIDA PARA DATOS FALTANTES:**\n> * Si para algún paso del análisis (ej. Alertas Estacionales, Proyección) no encontraste datos suficientes o los datos no cumplían los rangos establecidos, **debes indicarlo explícitamente en la sección correspondiente del informe.**\n> * **Ejemplo:** \"3. Alertas y Desviaciones Estacionales: No se dispone de un historial de 5 años para realizar una comparación estacional fiable. El análisis de desviaciones se omite por falta de datos suficientes para este periodo.\"\n\nDebes sintetizar TODOS los puntos de análisis (del 1 al 7) en esta narrativa justificada. Usa el siguiente formato con encabezados Markdown:\n\n---\n\n### Informe Estratégico de Producto: [Nombre del Producto]\n\n#### 1. Resumen Ejecutivo\n(Redacta un párrafo que resuma los hallazgos del \"Análisis Base\". Debe incluir el volumen total exportado, el valor, los mercados principales. **Explica la tendencia general narrando la comparación, ej: \"La tendencia general es positiva; al comparar [Año Reciente] con [Año Anterior], vemos un crecimiento del [X]% en valor...\"**)\n\n#### 2. Oportunidades Clave\n(Narra las \"Alertas de Oportunidad\" (Paso 2). **Justifica *por qué* son oportunidades narrando la comparación.** Ej: \"Hemos detectado una fuerte oportunidad en Alemania. **Primero, al comparar las importaciones de ese país desde Latinoamérica en [Periodo X] vs [Periodo Y]**, vimos un crecimiento del 15% (superando nuestro umbral de alerta del 3%). **Segundo, al contrastar nuestro precio CIF con el de [Competidor]**, encontramos que aún somos un 10% inferiores, dándonos margen...\")\n\n#### 3. Alertas y Desviaciones Estacionales\n(Presenta las \"Alertas Estacionales\" (Paso 3). **Explica la desviación narrando la comparación.** Ej: \"Atención: **Al comparar el volumen de [Mes Actual] ([X Toneladas]) con el promedio de los últimos 5 años para este mismo mes ([Y Toneladas])**, estamos un 5% por debajo. **El diagnóstico de esta anomalía indica que** el cliente "Cliente ABC" no ha repetido su pedido quincenal...\")\n\n#### 4. Proyección (Forecast)\n(Presenta el \"Análisis Predictivo\" (Paso 5). **Explica cómo se obtuvo la cifra.** Ej: \"Se proyecta un volumen de [X toneladas] para los próximos 3 meses. **Llegamos a esta cifra calculando** el promedio móvil de 5 años y **ajustándolo con** la estacionalidad típica de este trimestre. **Esta proyección, al compararla con el promedio histórico,** es un 12% superior...\")\n\n#### 5. Análisis de Precios y Competencia\n(Sintetiza los Pasos 4 y 6. **Explica las recomendaciones de precio narrando la comparación.** Ej: \"En el mercado de [País], nuestra posición es fuerte. Sin embargo, en [Otro País], **la comparación directa de nuestro precio CIF ([X USD]) frente al promedio del mercado ([Y USD]) revela que** somos un 15% superiores. Esta desventaja de precio está afectando el volumen, por lo que recomendamos un ajuste...\")\n\n#### 6. Recomendaciones EstratégICAS Clave\n(Lista las 3-5 \"Recomendaciones Estratégicas\" (Paso 7). **Cada justificación debe ser una síntesis clara de la comparación de datos que la respalda.**)\n\n* **[Acción 1]:** [Ej: \"Priorizar el mercado alemán\". **Justificación:** \"Basado en la **comparación de crecimiento** de importaciones (+15%) y nuestra **comparación de precio** (10% por debajo) identificadas en el Paso 2.\"]\n* **[Acción 2]:** [Ej: \"Ajustar precio en [Otro País]\". **Justificación:** \"Basado en la **comparación de nuestro precio CIF** que nos sitúa un 15% por encima del mercado (Paso 6), afectando el volumen.\"]\n* **[Acción 3]:** [Justificación clara basada en la **comparación de datos** de los pasos 1-6.].',
      //9ab24a7c-8634-42bd-882d-99de3bdd2885/page/p_sun55isjxd?params=%7B%22df49%22:%22include%25EE%2580%25800%25EE%2580%2580IN%25EE%2580%2580
      //text: 'Eres un agente con doble rol  primera Personalidad de Análisis (Interna): Eres un analista experto en comercio exterior de productos agroalimentarios ecuatorianos, especializado en análisis de oportunidades de mercado, detección de tendencias y recomendaciones estratégicas. Segunda Personalidad de Comunicación (Externa): Te comunicas como un estratega de negocios claro y directo. Tu misión es traducir tus análisis técnicos complejos en un informe narrativo para un público multifacético (gerencia, ventas, operaciones) usando un lenguaje común, fácil de entender e irrefutable. CONTEXTO Y CAPACIDADES: Tienes acceso a datos históricos de exportaciones ecuatorianas, importaciones de mercados destino, precios CIF, volúmenes, países competidores y estacionalidad. Debes analizar patrones, detectar anomalías y generar insights accionables. CUANDO RECIBAS UN SKU, DEBES: Realizar internamente todos los siguientes pasos de análisis para recopilar la información necesaria: ANÁLISIS BASE DEL PRODUCTO: Buscar información del producto (categoría,grupo, producto). Analizar la serie histórica de exportaciones (mínimo 3-5 años). Calcular métricas clave: total exportado (USD), volumen total (toneladas), N° de mercados, mercados Top 3, tendencia general y variaciones interanuales. ALERTAS DE OPORTUNIDAD: Buscar señales positivas: Tasa de crecimiento interanual > 3% en mercados destino. Mercados donde el destino importa >20% desde Latinoamérica. Precio de exportación de Ecuador inferior al de competidores. Identificar al menos 2 oportunidades. ALERTAS ESTACIONALES: Comparar cada mes del año en curso vs. promedio de los últimos 5 años. Identificar desviaciones > ±3% como anómalas. Investigar la causa probable (mercado, cliente, factores externos/internos). ANÁLISIS DE MERCADOS DETALLADO: Para los mercados principales, analizar: valor, volumen, precio promedio, participación de mercado, competidores y ventaja/desventaja de precio. ANÁLISIS PREDICTIVO (Forecasting 3-6 meses): Calcular proyección de toneladas basada en promedio móvil (5 años), estacionalidad y tendencia reciente. Generar una comparativa vs. el promedio histórico. ANÁLISIS PRESCRIPTIVO (Pricing): Comparar precio CIF de Ecuador vs. otros orígenes en mercados clave. Calcular diferencial porcentual vs. competidores. Recomendar ajustes si el precio es >10% superior al mercado o si hay oportunidad de captura de valor. RECOMENDACIONES ESTRATÉGICAS: Basado en todo lo anterior, formular 3-5 recomendaciones priorizadas (expansión, optimización de precio, etc.) con justificación. REGLAS CRÍTICAS DE ANÁLISIS: Usa EXCLUSIVAMENTE datos de los datastores conectados. Incluye números específicos con unidades (USD, kg, toneladas, %) en tu narración. Menciona periodos exactos (YYYY-MM o rangos de fechas). Si no encuentras datos suficientes, indícalo claramente. Tono: Tu análisis es de experto, pero tu redacción final debe ser clara, directa y fácil de entender para un público no técnico. FORMATO DE SALIDA: INFORME EJECUTIVO NARRATIVO NO GENERES UN JSON. Tu respuesta final debe ser un informe de texto cohesivo, fluido y con un hilo conductor. Debes sintetizar TODOS los puntos de análisis (del 1 al 7) en una narrativa lógica. Usa el siguiente formato con encabezados Markdown para estructurar tu informe: Informe Estratégico de Producto: [Nombre del Producto] 1. Resumen Ejecutivo (Redacta un párrafo que resuma los hallazgos del "Análisis Base". Debe incluir el volumen total exportado, el valor, los mercados principales, la tendencia general y la variación interanual.) 2. Oportunidades Clave (Narra las "Alertas de Oportunidad" más importantes. Ej: "Hemos detectado una fuerte oportunidad en Alemania, donde crecimos un 15% y nuestro precio es aún un 10% inferior al de [Competidor]...") 3. Alertas y Desviaciones Estacionales (Presenta las "Alertas Estacionales". Ej: "Atención: Este mes estamos un 5% por debajo del promedio histórico. El diagnóstico indica que el cliente \'Cliente ABC\' no ha repetido su pedido quincenal...") 4. Proyección (Forecast) (Presenta el "Análisis Predictivo". Ej: "Se proyecta un volumen de [X toneladas] para los próximos 3 meses. Esta cifra es un 12% superior al promedio de los últimos 5 años para este mismo periodo...") 5. Análisis de Precios y Competencia (Sintetiza el "Análisis Prescriptivo" y el "Análisis de Mercados". Ej: "En el mercado de [País], nuestra posición es fuerte. Sin embargo, en [Otro País], nuestro precio es un 15% superior al promedio del mercado, lo que está afectando el volumen. La recomendación es...") 6. Recomendaciones Estratégicas Clave (Lista las 3-5 "Recomendaciones Estratégicas" más importantes en formato de viñetas, explicando la justificación de cada una en lenguaje claro.) [Acción 1]: [Justificación clara basada en datos] [Acción 2]: [Justificación clara basada en datos] [Acción 3]: [Justificación clara basada en datos]',
      //55b733f0-0cd3-4a58-9262-ac86d9912517/page/p_sun55isjxd?params=%7B%22df28%22:%22include%25EE%2580%25800%25EE%2580%2580IN%25EE%2580%2580
      const fetchData = async () => {
        try {
          const response = await axios.post(
            "https://aiplatform.googleapis.com/v1/publishers/google/models/gemini-2.5-flash-lite:streamGenerateContent?key=AQ.Ab8RN6JipBh8PzdY1p0zJLoFC75S396MMeoXgBLSZ8pBz_KU2Q",
            {
              contents: [
                {
                  role: "user",
                  parts: [
                    {
                      text: categoria + " " + subcategoria + " " + producto,
                    },
                  ],
                },
              ],
              systemInstruction: {
                parts: [
                  {
                    text: 'Eres un analista experto en comercio exterior de agroalimentos ecuatorianos. Tu objetivo es generar un reporte estratégico de alto nivel, basado ESTRICTAMENTE en los datos proporcionados, priorizando la precisión. \n\n#### INSTRUCCIONES DE PROCESAMIENTO:\n1. **Origen:** Usa EXCLUSIVAMENTE los datastores conectados. NO INVENTES DATOS.\n2. **Vacíos:** Si falta información, escribe: "Datos insuficientes".\n3. **Lógica Rápida:** Oportunidad si crecimiento >3% o precio < competencia. Alerta Estacional si desviación > ±3% vs histórico. Pricing alerta si Precio Ecuador >10% sobre mercado.\n\n#### ESTRUCTURA DE SALIDA:\nGenera el informe con este formato exacto:\n\n### Informe Estratégico: [Nombre del Producto]\n\n#### 1. Radiografía del Producto\n* **Resumen:** [Volumen total] | [Valor total USD] | [Top 3 Mercados].\n* **Tendencia:** Describe la tendencia comparando periodo actual vs. anterior.\n\n#### 2. Oportunidades Detectadas (Criterio: Crecimiento >3% o Ventaja Precio)\n* **Mercado [País]:** [Nivel: ALTA/MEDIA].\n    * *Evidencia:* "Las importaciones crecieron un [X]% vs el año anterior" O "Nuestro precio es [X]% inferior al competidor".\n\n#### 3. Estacionalidad y Anomalías\n* **Estado:** [NORMAL / ANOMALÍA POSITIVA / NEGATIVA].\n* **Justificación:** Compara el volumen del mes actual vs. el promedio histórico.\n\n#### 4. Forecast y Estrategia de Precios\n* **Proyección:** [Volumen estimado corto plazo].\n* **Análisis de Precio:** Compara nuestro precio CIF ([X USD]) vs. Promedio Mercado ([Y USD]).\n* **Recomendación:** [MANTENER / AJUSTAR].\n\n#### 5. Acciones Recomendadas (Top 3)\n1. **[Acción]:** [Breve justificación basada en datos].\n2. **[Acción]:** [Breve justificación basada en datos].\n3. **[Acción]:** [Breve justificación basada en datos].',
                  },
                ],
              },
              generationConfig: {
                temperature: 0.2,
                maxOutputTokens: 65535,
                topP: 0.95,
                thinkingConfig: {
                  thinkingBudget: -1,
                },
              },
              safetySettings: [
                {
                  category: "HARM_CATEGORY_HATE_SPEECH",
                  threshold: "OFF",
                },
                {
                  category: "HARM_CATEGORY_DANGEROUS_CONTENT",
                  threshold: "OFF",
                },
                {
                  category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
                  threshold: "OFF",
                },
                {
                  category: "HARM_CATEGORY_HARASSMENT",
                  threshold: "OFF",
                },
              ],
              tools: [
                [
                  {
                    retrieval: {
                      vertexAiSearch: {
                        datastore:
                          "projects/anfab-ai-agroalimentario/locations/us/collections/default_collection/dataStores/exportaciones-datasur-anualdatabase_1760139510481",
                      },
                    },
                  },
                  {
                    retrieval: {
                      vertexAiSearch: {
                        datastore:
                          "projects/anfab-ai-agroalimentario/locations/us/collections/default_collection/dataStores/mercados-desagregadosdatabase_1760139252732",
                      },
                    },
                  },
                  {
                    retrieval: {
                      vertexAiSearch: {
                        datastore:
                          "projects/anfab-ai-agroalimentario/locations/us/collections/default_collection/dataStores/exportaciones-datasurdatabase_1760139210660",
                      },
                    },
                  },
                  {
                    retrieval: {
                      vertexAiSearch: {
                        datastore:
                          "projects/anfab-ai-agroalimentario/locations/us/collections/default_collection/dataStores/categorias-productosdatabase_1760139177632",
                      },
                    },
                  },
                  {
                    retrieval: {
                      vertexAiSearch: {
                        datastore:
                          "projects/anfab-ai-agroalimentario/locations/us/collections/default_collection/dataStores/exportaciones-bcedatabase_1760139146614",
                      },
                    },
                  },
                  {
                    retrieval: {
                      vertexAiSearch: {
                        datastore:
                          "projects/anfab-ai-agroalimentario/locations/us/collections/default_collection/dataStores/mercados-eeuu-uedatabase_1760138986422",
                      },
                    },
                  },
                ],
              ],
            }
          );
          console.log("DATA:", JSON.stringify(response.data));

          // Remover todos los objetos groundingMetadata
          const removeGroundingMetadata = (data: any): any => {
            if (Array.isArray(data)) {
              return data.map(removeGroundingMetadata);
            } else if (data && typeof data === "object") {
              const result: any = {};
              for (const key in data) {
                if (key !== "groundingMetadata") {
                  result[key] = removeGroundingMetadata(data[key]);
                }
              }
              return result;
            }
            return data;
          };

          const filteredData = removeGroundingMetadata(response.data);

          // Extraer y concatenar todos los campos 'text' de la respuesta
          let allText = "";
          const extractText = (obj: any): void => {
            if (obj && typeof obj === "object") {
              if (obj.text && typeof obj.text === "string") {
                allText += obj.text + " ";
              }
              Object.values(obj).forEach((value) => {
                if (typeof value === "object") {
                  extractText(value);
                }
              });
            }
          };
          extractText(filteredData);
          // Procesar el texto para formatear títulos con ##
          let formattedText = allText.trim();

          // Primero agregar salto de línea antes de cada '##'
          formattedText = formattedText.replace(/##/g, "\n##");

          // Convertir títulos con ## a negrita (formato HTML)
          formattedText = formattedText.replace(
            /##\s*([^\n]+)/g,
            "<strong>## $1</strong>"
          );

          formattedText = formattedText.replace(/###/g, "");
          formattedText = formattedText.replace(/##/g, "");
          formattedText = formattedText.replace(/#/g, "");

          // Convertir texto envuelto en ** a negritas
          formattedText = formattedText.replace(
            /\*\*([^*]+)\*\*/g,
            "<strong>$1</strong>"
          );

          setReportText(formattedText);
        } catch (error) {
          console.error("Error making POST request:", error);
        }
      };

      fetchData();
    }, [producto, navigate, categoria, subcategoria]);

    const goBack = () => {
      navigate(-1);
    };

    return (
      <div className="report-container">
        <div className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
          <button
            className="toggle-sidebar"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          >
            {isSidebarCollapsed ? "☰" : "✕"}
          </button>
          <nav className="sidebar-menu">
            <img
              src={logoWhite}
              alt="Logo"
              className="logo-white"
              onClick={() => {
                goBack();
              }}
              style={{ cursor: "pointer" }}
            />
            <button onClick={() => goBack()} className={"menu-item"}>
              Inicio
            </button>
            <button
              onClick={() => setSelectedTab(1)}
              className={selectedTab === 1 ? "menu-item active" : "menu-item"}
            >
              Portal IA
            </button>
            <button
              onClick={() => setSelectedTab(2)}
              className={selectedTab === 2 ? "menu-item active" : "menu-item"}
            >
              Reporte IA
            </button>
          </nav>
        </div>

        <div
          className={`report ${isSidebarCollapsed ? "sidebar-collapsed" : ""}`}
        >
          <div className="card">
            <div className="card-header">
              <div className="title-section">
                <img src={logoWhite} alt="Logo" className="logo" />
                <h1>Reporte de {producto}</h1>
                <div className="space-title"></div>
              </div>
              {categoria && subcategoria && (
                <div className="product-info">
                  <p>
                    <strong>Categoría:</strong> <span>{categoria} /</span>
                    <strong>Grupo:</strong> <span>{subcategoria} /</span>
                    <strong>Producto:</strong> <span>{producto}</span>
                  </p>
                </div>
              )}
            </div>

            <div className="card-body">
              {producto && (
                <div className={selectedTab === 1 ? "show" : "hide"}>
                  <h2>Bienvenido al Portal IA</h2>
                  <h3>Exploración Visual</h3>
                  <p>
                    Aquí comienza tu viaje para dominar el comercio global del
                    producto seleccionado. Este panel interactivo te ofrece un
                    análisis profundo y visual de sus dinámicas comerciales. A
                    continuación, verás cuatro gráficos esenciales que te
                    proporcionarán una visión completa: desde la estacionalidad
                    del volumen en mercados clave (EE. UU. y la UE) para
                    detectar ventanas de oportunidad, hasta la dinámica de
                    exportación que revela la concentración o diversificación de
                    tus destinos. Además, podrás monitorear la competitividad de
                    precios promedio frente a tus principales rivales y,
                    finalmente, comprender la especialización del mercado según
                    el tamaño de la empresa exportadora. Una base de información
                    clave para tus estrategias comerciales globales.
                  </p>
                  <div className="looker-dashboard">
                    <div className="chart-tabs-container">
                      <div className="chart-tabs-vertical">
                        <button
                          onClick={() => setSelectedChart(1)}
                          className={selectedChart === 1 ? "chart-tab active" : "chart-tab"}
                        >
                          Gráfico 1
                        </button>
                        <button
                          onClick={() => setSelectedChart(2)}
                          className={selectedChart === 2 ? "chart-tab active" : "chart-tab"}
                        >
                          Gráfico 2
                        </button>
                        <button
                          onClick={() => setSelectedChart(3)}
                          className={selectedChart === 3 ? "chart-tab active" : "chart-tab"}
                        >
                          Gráfico 3
                        </button>
                        <button
                          onClick={() => setSelectedChart(4)}
                          className={selectedChart === 4 ? "chart-tab active" : "chart-tab"}
                        >
                          Gráfico 4
                        </button>
                      </div>
                      <div className="chart-content">
                        {selectedChart === 1 && (
                          <iframe
                            title={`Dashboard de ${producto} - Gráfico 1`}
                            src={`https://lookerstudio.google.com/embed/reporting/9ab24a7c-8634-42bd-882d-99de3bdd2885/page/p_sun55isjxd?params=%7B%22df49%22:%22include%25EE%2580%25800%25EE%2580%2580IN%25EE%2580%2580${producto.replace(
                              / /g,
                              "%2520"
                            )}%22%7D`}
                            style={{ border: 0 }}
                          ></iframe>
                        )}
                        {selectedChart === 2 && (
                          <iframe
                            title={`Dashboard de ${producto} - Gráfico 2`}
                            src={`https://lookerstudio.google.com/embed/reporting/9ab24a7c-8634-42bd-882d-99de3bdd2885/page/p_t1kk8kdbyd?params=%7B%22df46%22:%22include%25EE%2580%25800%25EE%2580%2580IN%25EE%2580%2580${producto.replace(
                              / /g,
                              "%2520"
                            )}%22%7D`}
                            style={{ border: 0 }}
                          ></iframe>
                        )}
                        {selectedChart === 3 && (
                          <iframe
                            title={`Dashboard de ${producto} - Gráfico 3`}
                            src={`https://lookerstudio.google.com/embed/reporting/9ab24a7c-8634-42bd-882d-99de3bdd2885/page/p_ti7vozebyd?params=%7B%22df47%22:%22include%25EE%2580%25800%25EE%2580%2580IN%25EE%2580%2580${producto.replace(
                              / /g,
                              "%2520"
                            )}%22%7D`}
                            style={{ border: 0 }}
                          ></iframe>
                        )}
                        {selectedChart === 4 && (
                          <iframe
                            title={`Dashboard de ${producto} - Gráfico 4`}
                            src={`https://lookerstudio.google.com/embed/reporting/9ab24a7c-8634-42bd-882d-99de3bdd2885/page/p_0dbnyzebyd?params=%7B%22df48%22:%22include%25EE%2580%25800%25EE%2580%2580IN%25EE%2580%2580${producto.replace(
                              / /g,
                              "%2520"
                            )}%22%7D`}
                            style={{ border: 0 }}
                          ></iframe>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className={selectedTab === 2 ? "show" : "hide"}>
              {reportText ? (
                <>
                  <h2>Bienvenido al Reporte IA</h2>
                  <h3>Interpretación Experta</h3>
                  <p>
                    Soy AGroIA, el agente encargado de generar este análisis de
                    inteligencia de mercado para el producto que seleccionaste.
                    Mi objetivo es proporcionarte un resumen ejecutivo que
                    incluye cuatro tipos de alertas clave: oportunidad,
                    estacionalidad, pronóstico (forecasting) y recomendación de
                    precios (pricing). Debo ser transparente: la profundidad y
                    disponibilidad de estas alertas dependen directamente de si
                    los datos del producto son suficientes. Espero que la
                    información que te presente sea fácil de entender y de gran
                    utilidad estratégica.
                  </p>
                  <div className="report-content">
                    <button
                      onClick={() => window.print()}
                      className="export-button"
                    >
                      Generar PDF
                    </button>
                    <div
                      style={{ whiteSpace: "pre-line" }}
                      dangerouslySetInnerHTML={{ __html: reportText }}
                    />
                  </div>
                </>
              ) : (
                <p className="loading-text">
                  Cargando reporte para {producto}...
                </p>
              )}
            </div>
            {messengerLoaded && selectedTab === 1 && (
              <df-messenger
                project-id="anfab-ai-agroalimentario"
                agent-id="0f569fad-e333-46a2-b287-2adb08c871fd"
                language-code="es"
                max-query-length="-1"
                intent="WELCOME"
              >
                <df-messenger-chat-bubble chat-title="ANFAB AI chat box"></df-messenger-chat-bubble>
              </df-messenger>
            )}
          </div>
        </div>
      </div>
    );
};

export default Report;